import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AssignmentIndOutlined as RequestsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { APP_SHAPE } from '../../constants/shape';
import { getFleetDriverRequestsMonitor } from '../../services/monitoring.service';
import { useOrganizationStore } from '../../stores/organization.store';
import { OrganizationType } from '../../types/organization.type';
import type { DriverRequestStatus, FleetDriverRequestMonitor } from '../../types/monitoring.type';

const statuses: DriverRequestStatus[] = ['pending', 'accepted', 'rejected', 'expired', 'cancelled'];
const humanize = (value: string | null) => (value ?? '—').replace(/_/g, ' ');
const chipColor = (status: DriverRequestStatus) => ({ pending: 'warning', accepted: 'success', rejected: 'error', expired: 'default', cancelled: 'default' } as const)[status];

function CountCard({ label, count }: { label: string; count: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
      <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{label}</Typography>
      <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>{count}</Typography>
    </Paper>
  );
}

export default function DriverRequestsMonitor() {
  const { t, i18n } = useTranslation();
  const { selectedOrganization } = useOrganizationStore();
  const [requests, setRequests] = useState<FleetDriverRequestMonitor[]>([]);
  const [counts, setCounts] = useState<Partial<Record<DriverRequestStatus, number>>>({});
  const [status, setStatus] = useState<DriverRequestStatus | 'all'>('pending');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const number = useMemo(
    () => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language, i18n.resolvedLanguage],
  );
  const formatDate = (value: string | null) => value
    ? new Date(value).toLocaleString(i18n.resolvedLanguage || i18n.language)
    : '—';
  const translatedValue = (value: string | null) => {
    if (!value) return '—';
    return t(`monitoring.values.${value.toLowerCase()}`, { defaultValue: humanize(value) });
  };

  const fleetOrg = Boolean(selectedOrganization && [OrganizationType.RECYCLER, OrganizationType.TRUCK].includes(selectedOrganization.organization_type));

  useEffect(() => {
    if (!selectedOrganization || !fleetOrg) return;
    let active = true;
    setLoading(true);
    setError(null);
    getFleetDriverRequestsMonitor(selectedOrganization.id, page + 1, rowsPerPage, status === 'all' ? undefined : status)
      .then((result) => {
        if (!active) return;
        setRequests(result.assignments);
        setCounts(result.status_counts);
        setTotal(result.total);
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.detail ?? t('monitoring.driverRequests.loadError'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedOrganization, fleetOrg, page, rowsPerPage, status, refreshKey, t]);

  if (!selectedOrganization) return <Alert severity="info">{t('monitoring.driverRequests.selectOrganization')}</Alert>;
  if (!fleetOrg) return <Alert severity="info">{t('monitoring.driverRequests.wrongOrganizationType')}</Alert>;

  return (
    <Box>
      <PageHeader
        icon={<RequestsIcon />}
        title={t('monitoring.driverRequests.title')}
        subtitle={t('monitoring.driverRequests.subtitle', { name: selectedOrganization.name })}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={<Button startIcon={<RefreshIcon />} onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>{t('common.refresh')}</Button>}
      />

      <Alert severity="info" sx={{ mb: 2 }}>{t('monitoring.driverRequests.monitoringOnly')}</Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 2 }}>
        {statuses.map((item) => <CountCard key={item} label={translatedValue(item)} count={counts[item] ?? 0} />)}
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: `${APP_SHAPE.radiusLg}px` }} variant="outlined">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="request-status-label">{t('monitoring.driverRequests.requestStatus')}</InputLabel>
          <Select labelId="request-status-label" label={t('monitoring.driverRequests.requestStatus')} value={status} onChange={(event) => { setStatus(event.target.value as DriverRequestStatus | 'all'); setPage(0); }}>
            <MenuItem value="all">{t('monitoring.driverRequests.allRequests')}</MenuItem>
            {statuses.map((item) => <MenuItem key={item} value={item} sx={{ textTransform: 'capitalize' }}>{translatedValue(item)}</MenuItem>)}
          </Select>
        </FormControl>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('monitoring.driverRequests.columns.request')}</TableCell>
              <TableCell>{t('monitoring.driverRequests.columns.driver')}</TableCell>
              <TableCell>{t('monitoring.driverRequests.columns.truck')}</TableCell>
              <TableCell>{t('monitoring.columns.wasteOutlet')}</TableCell>
              <TableCell>{t('monitoring.driverRequests.columns.destinationRecycler')}</TableCell>
              <TableCell>{t('monitoring.columns.material')}</TableCell>
              <TableCell align="right">{t('monitoring.columns.quantity')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell>{t('monitoring.driverRequests.columns.sentResponse')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && requests.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}>{status === 'pending' ? t('monitoring.driverRequests.emptyPending') : t('monitoring.driverRequests.empty')}</TableCell></TableRow>
            )}
            {requests.map((request) => (
              <TableRow hover key={request.id}>
                <TableCell><Typography variant="body2" fontWeight={600}>#{request.pickup_order_id.slice(0, 8)}</Typography><Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{translatedValue(request.pickup_status)}</Typography></TableCell>
                <TableCell>{request.driver_name}</TableCell>
                <TableCell>{request.truck_name}</TableCell>
                <TableCell>{request.waste_outlet_name}</TableCell>
                <TableCell>{request.destination_name ?? '—'}</TableCell>
                <TableCell><Typography variant="body2">{request.material_name}</Typography><Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{translatedValue(request.waste_type)}</Typography></TableCell>
                <TableCell align="right">{number.format(request.amount)} {request.amount_unit ?? ''}</TableCell>
                <TableCell><Chip size="small" label={translatedValue(request.status)} color={chipColor(request.status)} /></TableCell>
                <TableCell><Typography variant="body2">{formatDate(request.assigned_at)}</Typography><Typography variant="caption" color="text.secondary">{request.responded_at ? t('monitoring.driverRequests.respondedAt', { date: formatDate(request.responded_at) }) : t('monitoring.driverRequests.expiresAt', { date: formatDate(request.expires_at) })}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, value) => setPage(value)}
          onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage={t('monitoring.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('monitoring.pagination.displayedRows', { from, to, count })}
          getItemAriaLabel={(type) => t(`monitoring.pagination.${type}Page`)}
        />
      </TableContainer>
    </Box>
  );
}
